import React, { useEffect, useMemo, useState } from "react";
import { FaPlus, FaSave, FaSearch, FaUpload } from "react-icons/fa";
import PageHeader from "src/components/pageHeader";
import PageLoading from "src/components/pageLoading";
import { User } from "src/entities/user.entity";
import { getCookieWithKey } from "src/utils/browserStorage.util";
import { getLangNameByLangCode } from "src/utils/util";
import { showToast } from "src/utils/toast.util";
import {
  useGetAICoachLevelVocabulariesQuery,
  useUpsertAICoachLevelVocabularyMutation,
} from "../ai-coach-level-vocabulary.service";
import { ImportLevelVocabulariesYamlModal } from "../components/import-level-vocabularies-yaml-modal";
import {
  AICoachLevelVocabulary,
  AICoachLevelVocabularyLevels,
} from "../entities/ai-coach-level-vocabulary.entity";
import {
  AI_COACH_LEVELS,
  createEmptyLevelVocabularyLevels,
  normalizeLangCode,
  sanitizeLevelVocabularyLevels,
} from "../utils/level-vocabulary";

type LevelVocabularyRow = AICoachLevelVocabulary & { id: string };
type DraftVocabulary = {
  langCode: string;
  levels: AICoachLevelVocabularyLevels;
};

const createDraftVocabulary = (langCode: string, levels?: AICoachLevelVocabularyLevels): DraftVocabulary => ({
  langCode,
  levels: levels ? { ...levels } : createEmptyLevelVocabularyLevels(),
});

const areLevelsEqual = (left: AICoachLevelVocabularyLevels, right: AICoachLevelVocabularyLevels) =>
  AI_COACH_LEVELS.every((level) => left[level] === right[level]);

const formatTimestamp = (value?: { toDate?: () => Date } | Date | null) => {
  if (!value) return "Never";
  const date = value instanceof Date ? value : value?.toDate?.();
  if (!date) return "Never";
  return date.toLocaleString();
};

const AICoachLevelVocabularyPage = () => {
  const [query, setQuery] = useState("");
  const [selectedLangCode, setSelectedLangCode] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftVocabulary | null>(null);
  const [newLangCode, setNewLangCode] = useState("");
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const { data: vocabularies = [], isLoading, isError } = useGetAICoachLevelVocabulariesQuery();
  const [upsertLevelVocabulary, { isLoading: isSaving }] = useUpsertAICoachLevelVocabularyMutation();

  const currentAdmin = useMemo(() => {
    try {
      return getCookieWithKey("current-admin") as User;
    } catch (error) {
      return undefined;
    }
  }, []);

  const sortedVocabularies = useMemo(
    () => [...vocabularies].sort((left, right) => left.langCode.localeCompare(right.langCode)),
    [vocabularies]
  );

  const filteredVocabularies = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return sortedVocabularies.filter((item) => {
      if (!normalizedQuery) return true;

      const haystack = [
        item.langCode,
        getLangNameByLangCode(item.langCode),
        ...AI_COACH_LEVELS.map((level) => item.levels[level]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [query, sortedVocabularies]);

  const selectedDocument = useMemo(
    () => sortedVocabularies.find((item) => item.langCode === selectedLangCode) || null,
    [selectedLangCode, sortedVocabularies]
  );

  const selectedDocumentLevelsSignature = useMemo(
    () => AI_COACH_LEVELS.map((level) => selectedDocument?.levels[level] || "").join("\u0000"),
    [selectedDocument]
  );

  useEffect(() => {
    if (selectedLangCode) return;
    if (!sortedVocabularies.length) return;
    setSelectedLangCode(sortedVocabularies[0].langCode);
  }, [selectedLangCode, sortedVocabularies]);

  useEffect(() => {
    if (!selectedLangCode) {
      setDraft((current) => (current === null ? current : null));
      return;
    }

    const nextDraft = selectedDocument
      ? createDraftVocabulary(selectedDocument.langCode, selectedDocument.levels)
      : createDraftVocabulary(selectedLangCode);

    setDraft((current) => {
      if (
        current &&
        current.langCode === nextDraft.langCode &&
        areLevelsEqual(current.levels, nextDraft.levels)
      ) {
        return current;
      }

      return nextDraft;
    });
  }, [selectedDocument, selectedDocumentLevelsSignature, selectedLangCode]);

  const visibleVocabularies = useMemo(() => {
    if (!selectedLangCode) return filteredVocabularies;

    const selectedExists = filteredVocabularies.some((item) => item.langCode === selectedLangCode);
    const isDraftOnly = !sortedVocabularies.some((item) => item.langCode === selectedLangCode);

    if (!selectedExists && isDraftOnly) {
      return [
        {
          id: selectedLangCode,
          langCode: selectedLangCode,
          levels: createEmptyLevelVocabularyLevels(),
        } as LevelVocabularyRow,
        ...filteredVocabularies,
      ];
    }

    return filteredVocabularies;
  }, [filteredVocabularies, selectedLangCode, sortedVocabularies]);

  const isDirty = useMemo(() => {
    if (!draft) return false;
    if (!selectedDocument) {
      return AI_COACH_LEVELS.some((level) => draft.levels[level].trim() !== "");
    }

    const savedLevels = sanitizeLevelVocabularyLevels(selectedDocument.levels);
    const draftLevels = sanitizeLevelVocabularyLevels(draft.levels);

    return AI_COACH_LEVELS.some((level) => savedLevels[level] !== draftLevels[level]);
  }, [draft, selectedDocument]);

  const handleCreateDraft = () => {
    const normalizedLangCode = normalizeLangCode(newLangCode);
    if (!normalizedLangCode) {
      showToast("warrning", "Please enter a language code first.");
      return;
    }

    setSelectedLangCode(normalizedLangCode);
    setNewLangCode("");

    if (sortedVocabularies.some((item) => item.langCode === normalizedLangCode)) {
      showToast("success", "Opened the existing language configuration.");
      return;
    }

    showToast("success", `Created a new draft for ${normalizedLangCode.toUpperCase()}.`);
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!draft) {
      showToast("warrning", "Please choose or create a language configuration first.");
      return;
    }

    try {
      await upsertLevelVocabulary({
        langCode: normalizeLangCode(draft.langCode),
        levels: sanitizeLevelVocabularyLevels(draft.levels),
        updatedByAdminId: currentAdmin?.id,
        updatedByAdminName: currentAdmin?.name,
      }).unwrap();

      showToast("success", "Level vocabulary saved successfully.");
    } catch (error) {
      showToast(
        "error",
        (error as { error?: string })?.error || "Failed to save AI coach level vocabulary."
      );
    }
  };

  if (isLoading) {
    return <PageLoading />;
  }

  if (isError) {
    return (
      <div className="page_container space-y-6">
        <PageHeader text="AI Coach Level Vocabulary" />
        <div className="rounded-[28px] border border-red-200 bg-red-50 p-8 text-center">
          <h2 className="text-xl font-semibold text-red-800">Unable to load AI coach level vocabulary</h2>
          <p className="mt-2 text-sm text-red-700">
            The dashboard could not fetch level vocabulary documents from Firestore. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page_container space-y-8 pb-8">
      <PageHeader text="AI Coach Level Vocabulary" />

      <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-950 px-5 py-4 text-white">
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_200px_140px_auto] xl:items-end">
            <label className="block min-w-0">
              <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                Search
              </span>
              <span className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <FaSearch className="text-slate-400" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search langCode or vocabulary"
                  className="w-full border-0 bg-transparent text-sm text-white outline-none placeholder:text-slate-500"
                />
              </span>
            </label>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleCreateDraft();
              }}
              className="contents"
            >
              <label className="block min-w-0">
                <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  LangCode
                </span>
                <input
                  value={newLangCode}
                  onChange={(event) => setNewLangCode(event.target.value)}
                  placeholder="nl"
                  className="w-full rounded-2xl border border-white/10 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-cyan-400"
                />
              </label>

              <button
                type="submit"
                className="inline-flex h-[50px] items-center justify-center gap-2 rounded-2xl bg-cyan-400 px-4 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300"
              >
                <FaPlus className="text-[11px]" />
                Open
              </button>
            </form>

            <button
              type="button"
              onClick={() => setIsImportModalOpen(true)}
              className="inline-flex h-[50px] items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              <FaUpload className="text-[11px]" />
              Import YAML
            </button>
          </div>
        </div>

        <div className="grid xl:grid-cols-[320px_minmax(0,1fr)]">
          <div className="border-b border-slate-200 xl:border-b-0 xl:border-r">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h2 className="text-sm font-semibold text-slate-950">Languages</h2>
                <p className="text-xs text-slate-500">{visibleVocabularies.length} items</p>
              </div>
              {selectedLangCode ? (
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 font-mono text-[11px] font-semibold text-slate-700">
                    {selectedLangCode.toUpperCase()}
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                      draft ? (isDirty ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900") : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {draft ? (isDirty ? "Unsaved" : "Saved") : "-"}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="max-h-[760px] overflow-y-auto p-3">
              {visibleVocabularies.length ? (
                <div className="space-y-1.5">
                  {visibleVocabularies.map((item) => {
                    const populatedLevels = AI_COACH_LEVELS.filter((level) => item.levels[level].trim()).length;
                    const isSelected = item.langCode === selectedLangCode;
                    const isDraftOnly = !sortedVocabularies.some((savedItem) => savedItem.langCode === item.langCode);

                    return (
                      <button
                        key={item.langCode}
                        type="button"
                        onClick={() => setSelectedLangCode(item.langCode)}
                        className={`w-full rounded-2xl border px-3 py-3 text-left transition ${
                          isSelected
                            ? "border-cyan-200 bg-cyan-50 text-slate-950 shadow-sm"
                            : "border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-mono text-[13px] font-semibold text-inherit">
                                {item.langCode.toUpperCase()}
                              </span>
                              {isDraftOnly ? (
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
                                  Draft
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-1 text-xs text-slate-500">{getLangNameByLangCode(item.langCode)}</p>
                          </div>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isSelected ? "bg-white text-slate-700" : "bg-slate-100 text-slate-700"}`}>
                            {populatedLevels}/6
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center">
                  <p className="text-sm font-semibold text-slate-900">No data</p>
                </div>
              )}
            </div>
          </div>

          <div className="min-w-0">
            {draft ? (
              <form onSubmit={handleSave} className="h-full">
                <section className="h-full bg-white">
                  <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="font-mono text-lg font-semibold text-slate-950">{draft.langCode.toUpperCase()}</h2>
                        <span className="text-sm text-slate-500">{getLangNameByLangCode(draft.langCode)}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span>Updated: {formatTimestamp(selectedDocument?.updatedAt)}</span>
                        <span>By: {selectedDocument?.updatedByAdminName || "Unknown"}</span>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSaving}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <FaSave className="text-[11px]" />
                      {isSaving ? "Saving..." : "Save"}
                    </button>
                  </div>

                  <div className="space-y-4 p-5">
                    {AI_COACH_LEVELS.map((level) => (
                      <div key={level} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <label htmlFor={`level-${level}`} className="font-semibold text-slate-900">
                            {level}
                          </label>
                          <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-500">
                            {draft.levels[level].length.toLocaleString()} chars
                          </span>
                        </div>
                        <textarea
                          id={`level-${level}`}
                          value={draft.levels[level]}
                          onChange={(event) =>
                            setDraft((current) =>
                              current
                                ? {
                                    ...current,
                                    levels: {
                                      ...current.levels,
                                      [level]: event.target.value,
                                    },
                                  }
                                : current
                          )
                        }
                        placeholder="Comma-separated words"
                          className="min-h-[220px] w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 font-mono text-[12px] leading-6 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                        />
                      </div>
                    ))}
                  </div>
                </section>
              </form>
            ) : (
              <section className="flex min-h-[420px] items-center justify-center bg-slate-50 text-center">
                <h2 className="text-lg font-semibold text-slate-950">Select a language</h2>
              </section>
            )}
          </div>
        </div>
      </section>

      <ImportLevelVocabulariesYamlModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
      />
    </div>
  );
};

export default AICoachLevelVocabularyPage;
